import { IMPLEMENTED_RUNTIME_TYPES, type Agent, type RuntimeProfile } from '@deskworks/shared-types';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { ModelField, supportsModel } from '@/components/model-field';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage } from '@/lib/api';
import { useAgents, useRuntimeProfiles } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { agentRuntimeLabel, profileLabel } from '@/lib/labels';
import { qk } from '@/lib/query-keys';

const AUTOMATIC = 'automatic';

/** A runtime agents can actually run on (active and supported by the worker). */
export function isUsableRuntime(p: RuntimeProfile | null | undefined): p is RuntimeProfile {
  return !!p && p.status === 'active' && p.implemented !== false && IMPLEMENTED_RUNTIME_TYPES.includes(p.runtimeType);
}

/** Lead Agents must be active and have a usable runtime (the backend enforces this too). */
export function canLead(agent: Agent, profiles: RuntimeProfile[] | undefined): boolean {
  const profile = profiles?.find((p) => p.id === agent.runtimeProfileId) ?? agent.runtimeProfile ?? null;
  return agent.status === 'active' && isUsableRuntime(profile);
}

/**
 * Lead Agent select for a workspace: only agents with an active runtime are selectable; others are shown
 * greyed out. Offers "+ New agent" inline, and explains what to do when the workspace has no runtime at all.
 */
export function LeadAgentPicker({
  workspaceId,
  value,
  onChange,
  exclude,
  size = 'default',
}: {
  workspaceId: string;
  value: string;
  onChange: (agentId: string) => void;
  /** Agent ids not to list (e.g. the current lead). */
  exclude?: string[];
  size?: 'sm' | 'default';
}) {
  const agents = useAgents(workspaceId);
  const profiles = useRuntimeProfiles(workspaceId);
  const [creating, setCreating] = useState(false);

  if (!workspaceId) {
    return <p className="text-xs text-muted-foreground">{t('Choose a Workspace first.')}</p>;
  }
  const usableRuntimes = (profiles.data ?? []).filter(isUsableRuntime);
  const list = (agents.data ?? []).filter((a) => !exclude?.includes(a.id));
  const profileOf = (a: Agent) => profiles.data?.find((p) => p.id === a.runtimeProfileId) ?? a.runtimeProfile ?? null;

  if (profiles.isSuccess && usableRuntimes.length === 0) {
    return (
      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-[13px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
        {t('This workspace has no runtime yet, so no agent can lead a desk. Add one first:')}{' '}
        <Link to="/w/$workspaceId/settings" params={{ workspaceId }} search={{ tab: 'runtime-profiles' }} className="font-medium underline">
          {t('Settings → Runtimes → New runtime')}
        </Link>
      </div>
    );
  }

  return (
    <div className="grid gap-2">
      {!creating && (
        <div className="flex gap-2">
          <Select value={value} onValueChange={onChange}>
            <SelectTrigger size={size} className="min-w-0 flex-1">
              <SelectValue placeholder={list.length ? t('Choose a Lead Agent') : t('No agents yet — create one')} />
            </SelectTrigger>
            <SelectContent>
              {list.map((a) => {
                const ok = canLead(a, profiles.data);
                const profile = profileOf(a);
                return (
                  <SelectItem key={a.id} value={a.id} disabled={!ok}>
                    {a.name}
                    <span className="ml-1.5 text-xs text-muted-foreground">
                      {ok ? `· ${agentRuntimeLabel(a, profile)}` : a.status !== 'active' ? `· ${t('disabled')}` : `· ${t('no runtime')}`}
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
          <Button type="button" variant="outline" size={size === 'sm' ? 'sm' : 'default'} onClick={() => setCreating(true)}>
            <Plus /> {t('New agent')}
          </Button>
        </div>
      )}
      {creating && (
        <NewAgentForm
          workspaceId={workspaceId}
          runtimes={usableRuntimes}
          onCancel={() => setCreating(false)}
          onCreated={(a) => {
            setCreating(false);
            onChange(a.id);
          }}
        />
      )}
    </div>
  );
}

function NewAgentForm({
  workspaceId,
  runtimes,
  onCancel,
  onCreated,
}: {
  workspaceId: string;
  runtimes: RuntimeProfile[];
  onCancel: () => void;
  onCreated: (agent: Agent) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [instructions, setInstructions] = useState('');
  // "Automatic" by default: the best connected runtime on every run (docs/features/agents-and-runtimes.md).
  const [runtimeId, setRuntimeId] = useState(AUTOMATIC);
  const [model, setModel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const runtime = runtimes.find((p) => p.id === runtimeId);

  // Not a nested <form>: this lives inside the create-desk form.
  const create = async () => {
    if (!name.trim() || !runtimeId) return;
    setSaving(true);
    setError(null);
    try {
      const agent = await api.agents.create(workspaceId, {
        name: name.trim(),
        instructions: instructions.trim() || undefined,
        runtimeProfileId: runtimeId === AUTOMATIC ? null : runtimeId,
        model: runtime && supportsModel(runtime.runtimeType) ? model.trim() || null : null,
      });
      await queryClient.invalidateQueries({ queryKey: qk.agents(workspaceId) });
      onCreated(agent);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid gap-3 rounded-lg border bg-muted/30 p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">{t('New agent')}</span>
        <Button type="button" variant="ghost" size="icon-sm" className="size-7" onClick={onCancel} aria-label={t('Cancel new agent')}>
          <X />
        </Button>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="new-agent-name">{t('Name')}</Label>
        <Input id="new-agent-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('e.g. Support Lead')} autoFocus />
      </div>
      <div className="grid gap-1.5">
        <Label>{t('Runtime')}</Label>
        <Select value={runtimeId} onValueChange={setRuntimeId}>
          <SelectTrigger>
            <SelectValue placeholder={t('Choose a runtime')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={AUTOMATIC}>{t('Automatic (recommended)')}</SelectItem>
            {runtimes.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {profileLabel(p)}
                {p.runtimeType !== 'local' && !p.hasCredential && <span className="ml-1.5 text-xs text-muted-foreground">· {t('not connected yet')}</span>}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {runtime && supportsModel(runtime.runtimeType) && <ModelField value={model} onChange={setModel} profile={runtime} />}
      <div className="grid gap-1.5">
        <Label htmlFor="new-agent-instructions">
          {t('Instructions')} <span className="font-normal text-muted-foreground">({t('optional')})</span>
        </Label>
        <Textarea
          id="new-agent-instructions"
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={3}
          placeholder={t('How should this agent work? You can change this later.')}
        />
      </div>
      {error && <p className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</p>}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          {t('Cancel')}
        </Button>
        <Button type="button" size="sm" loading={saving} disabled={!name.trim() || !runtimeId} onClick={() => void create()}>
          {t('Create agent')}
        </Button>
      </div>
    </div>
  );
}
